/**
 * Supported timezone options for the application
 * Maps timezone display names to IANA timezone identifiers
 */
export enum Timezone {
  NEWFOUNDLAND = 'America/St_Johns',
  ATLANTIC = 'America/Halifax',
  EASTERN = 'America/New_York',
  CENTRAL = 'America/Chicago',
  SASKATCHEWAN = 'America/Regina',
  MOUNTAIN = 'America/Denver',
  ALBERTA = 'America/Edmonton',
  ARIZONA = 'America/Phoenix',
  YUKON = 'America/Whitehorse',
  PACIFIC = 'America/Los_Angeles',
  PACIFIC_VANCOUVER = 'America/Vancouver',
  ALASKA = 'America/Anchorage',
  HAWAII = 'Pacific/Honolulu',
}

/**
 * Timezone display names mapped to their IANA identifiers, ordered East to West.
 * Regions that do not observe DST are marked with (No DST).
 */
export const TIMEZONE_OPTIONS = [
  { name: "Newfoundland Time - St. John's", value: Timezone.NEWFOUNDLAND },
  { name: 'Atlantic Time - Halifax', value: Timezone.ATLANTIC },
  { name: 'Eastern Time - New York, Toronto', value: Timezone.EASTERN },
  { name: 'Central Time - Chicago, Winnipeg', value: Timezone.CENTRAL },
  { name: 'Saskatchewan - Regina (No DST)', value: Timezone.SASKATCHEWAN },
  { name: 'Mountain Time - Denver', value: Timezone.MOUNTAIN },
  { name: 'Mountain Time - Edmonton', value: Timezone.ALBERTA },
  { name: 'Arizona - Phoenix (No DST)', value: Timezone.ARIZONA },
  { name: 'Yukon - Whitehorse (No DST)', value: Timezone.YUKON },
  { name: 'Pacific Time - Los Angeles', value: Timezone.PACIFIC },
  {
    name: 'Pacific Time - Vancouver',
    value: Timezone.PACIFIC_VANCOUVER,
  },
  { name: 'Alaska Time - Anchorage', value: Timezone.ALASKA },
  { name: 'Hawaii Time - Honolulu (No DST)', value: Timezone.HAWAII },
] as const;

/**
 * Type-safe timezone option
 */
export type TimezoneOption = (typeof TIMEZONE_OPTIONS)[number];

/**
 * Supported date format options for the application
 */
export enum DateFormat {
  /**
   * US date format: MM/DD/YY
   * Example: 12/31/25
   */
  US = 'US',

  /**
   * Canadian/ISO date format: YYYY-MM-DD
   * Example: 2025-12-31
   */
  CANADIAN = 'CANADIAN',
}

/**
 * Date format display information
 */
export const DATE_FORMAT_OPTIONS = [
  {
    format: DateFormat.US,
    displayName: 'US Format',
    pattern: 'MM/DD/YY',
    example: '12/31/25',
  },
  {
    format: DateFormat.CANADIAN,
    displayName: 'Canada/ISO Standard Format',
    pattern: 'YYYY-MM-DD',
    example: '2025-12-31',
  },
] as const;

/**
 * Type-safe date format option
 */
export type DateFormatOption = (typeof DATE_FORMAT_OPTIONS)[number];

/**
 * Combined date/time settings interface
 */
export interface DateTimeSettings {
  timezone: Timezone;
  dateFormat: DateFormat;
}
